import { useState, useEffect } from 'react';
import { deviceController } from '../../controllers/deviceController';
import Device from '../../models/Device';
import { Plus, Search, Download, Eye, Edit2, Trash2, X, Battery, Tablet } from 'lucide-react';

const EMPTY_FORM = {
  deviceName: '', serialNumber: '', platform: 'iOS', osVersion: '',
  model: '', manufacturer: 'Apple', imei: '', macAddress: '',
  assignedTo: '', department: '', group: '', status: 'Active',
  complianceStatus: 'Compliant', batteryLevel: 100,
  storageUsed: 0, storageTotal: 128,
  isEncrypted: true, isLocked: false,
  location: '', ipAddress: '', policyName: '', notes: ''
};

const IOSDevicesPage = () => {
  const [devices, setDevices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showView, setShowView] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchDevices(); }, []);

  useEffect(() => {
    let result = [...devices];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(d => d.deviceName?.toLowerCase().includes(q) || d.serialNumber?.toLowerCase().includes(q) || d.assignedTo?.toLowerCase().includes(q) || d.model?.toLowerCase().includes(q));
    }
    if (filterStatus !== 'All') result = result.filter(d => d.status === filterStatus);
    setFiltered(result);
    setCurrentPage(1);
  }, [devices, searchQuery, filterStatus]);

  const fetchDevices = async () => {
    setLoading(true);
    const result = await deviceController.getByPlatform('iOS');
    if (result.success) setDevices(result.data);
    setLoading(false);
  };

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setFormErrors([]);
  };

  const openAdd = () => { setFormData(EMPTY_FORM); setFormErrors([]); setShowAdd(true); };

  const handleAddSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true); setFormErrors([]);
    const result = await deviceController.create({ ...formData, platform: 'iOS' });
    if (result.success) { setShowAdd(false); fetchDevices(); }
    else setFormErrors(result.errors || [result.message]);
    setSubmitting(false);
  };

  const openEdit = (device) => { setSelected(device); setFormData({ ...device }); setFormErrors([]); setShowEdit(true); };

  const handleEditSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true); setFormErrors([]);
    const result = await deviceController.update(selected.id, formData);
    if (result.success) { setShowEdit(false); fetchDevices(); }
    else setFormErrors(result.errors || [result.message]);
    setSubmitting(false);
  };

  const openDelete = (device) => { setSelected(device); setShowDelete(true); };

  const handleDelete = async () => {
    const result = await deviceController.delete(selected.id || selected._id);
    if (result.success) { setShowDelete(false); fetchDevices(); }
    else alert('Error: ' + result.message);
  };

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const s = {
    page: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    titleWrap: { display: 'flex', alignItems: 'center', gap: '12px' },
    icon: { width: '44px', height: '44px', borderRadius: '12px', backgroundColor: '#007aff20', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { fontSize: '26px', fontWeight: '700', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
    btnPrimary: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', backgroundColor: '#007aff', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    btnSecond: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    btnDanger: { backgroundColor: '#ef4444', color: '#fff', border: 'none' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px', marginBottom: '22px' },
    statCard: { backgroundColor: '#fff', padding: '18px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: '3px solid #007aff' },
    statLabel: { fontSize: '12px', color: '#64748b', marginBottom: '6px' },
    statVal: { fontSize: '26px', fontWeight: '700', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap' },
    searchWrap: { flex: 1, minWidth: '220px', position: 'relative' },
    searchIn: { width: '100%', padding: '10px 10px 10px 38px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    searchIco: { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' },
    select: { padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '13px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
    td: { padding: '13px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
    actBtn: { padding: '5px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' },
    pager: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #e2e8f0' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    mContent: { backgroundColor: '#fff', borderRadius: '14px', width: '90%', maxWidth: '660px', maxHeight: '90vh', overflow: 'auto' },
    mHeader: { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 },
    mTitle: { fontSize: '18px', fontWeight: '600', color: '#1e293b', margin: 0 },
    mBody: { padding: '24px' },
    mFooter: { padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px', position: 'sticky', bottom: 0, backgroundColor: '#fff' },
    fGroup: { marginBottom: '16px' },
    label: { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', resize: 'vertical', minHeight: '80px', boxSizing: 'border-box' },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    errBox: { backgroundColor: '#fee2e2', borderRadius: '8px', padding: '12px', marginBottom: '16px' },
    errItem: { color: '#dc2626', fontSize: '13px', marginBottom: '3px' },
    sectionHd: { fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '14px' },
    divider: { margin: '18px 0', borderTop: '1px solid #e2e8f0' },
    progBar: { height: '5px', borderRadius: '3px', backgroundColor: '#e2e8f0', overflow: 'hidden', marginTop: '4px' },
    detLabel: { fontSize: '11px', color: '#64748b', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.05em' },
    detVal: { fontSize: '14px', fontWeight: '500', color: '#1e293b' },
  };

  const FormBody = () => (
    <div style={s.mBody}>
      {formErrors.length > 0 && <div style={s.errBox}>{formErrors.map((e, i) => <div key={i} style={s.errItem}>• {e}</div>)}</div>}
      <div style={s.sectionHd}>Device Info</div>
      <div style={s.grid2}>
        <div style={s.fGroup}><label style={s.label}>Device Name *</label><input name="deviceName" style={s.input} value={formData.deviceName} onChange={handleInput} placeholder="e.g. iPhone 15 Pro" required /></div>
        <div style={s.fGroup}><label style={s.label}>Serial Number *</label><input name="serialNumber" style={s.input} value={formData.serialNumber} onChange={handleInput} required /></div>
        <div style={s.fGroup}><label style={s.label}>Model *</label><input name="model" style={s.input} value={formData.model} onChange={handleInput} placeholder="e.g. iPhone 15 Pro Max" required /></div>
        <div style={s.fGroup}><label style={s.label}>iOS Version</label><input name="osVersion" style={s.input} value={formData.osVersion} onChange={handleInput} placeholder="e.g. iOS 17.4" /></div>
        <div style={s.fGroup}><label style={s.label}>IMEI</label><input name="imei" style={s.input} value={formData.imei} onChange={handleInput} /></div>
        <div style={s.fGroup}><label style={s.label}>Storage Total (GB)</label><input type="number" name="storageTotal" style={s.input} value={formData.storageTotal} onChange={handleInput} /></div>
      </div>
      <div style={s.divider} />
      <div style={s.sectionHd}>Assignment & Status</div>
      <div style={s.grid2}>
        <div style={s.fGroup}><label style={s.label}>Assigned To</label><input name="assignedTo" style={s.input} value={formData.assignedTo} onChange={handleInput} /></div>
        <div style={s.fGroup}><label style={s.label}>Department</label><input name="department" style={s.input} value={formData.department} onChange={handleInput} /></div>
        <div style={s.fGroup}><label style={s.label}>Status</label>
          <select name="status" style={s.input} value={formData.status} onChange={handleInput}>
            <option value="Active">Active</option><option value="Inactive">Inactive</option><option value="Lost">Lost</option><option value="Retired">Retired</option>
          </select>
        </div>
        <div style={s.fGroup}><label style={s.label}>Compliance</label>
          <select name="complianceStatus" style={s.input} value={formData.complianceStatus} onChange={handleInput}>
            <option value="Compliant">Compliant</option><option value="Non-Compliant">Non-Compliant</option><option value="Unknown">Unknown</option>
          </select>
        </div>
        <div style={s.fGroup}><label style={s.label}>Battery (%)</label><input type="number" name="batteryLevel" style={s.input} value={formData.batteryLevel} onChange={handleInput} min="0" max="100" /></div>
        <div style={s.fGroup}><label style={s.label}>Storage Used (GB)</label><input type="number" name="storageUsed" style={s.input} value={formData.storageUsed} onChange={handleInput} min="0" /></div>
      </div>
      <div style={s.fGroup}><label style={s.label}>Notes</label><textarea name="notes" style={s.textarea} value={formData.notes} onChange={handleInput} /></div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.titleWrap}>
          <div style={s.icon}><Tablet size={22} color="#007aff" /></div>
          <div><h1 style={s.title}>iOS Devices</h1><div style={s.subtitle}>{devices.length} Apple devices enrolled</div></div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={{ ...s.btnPrimary, ...s.btnSecond }}><Download size={15} /> Export</button>
          <button style={s.btnPrimary} onClick={openAdd}><Plus size={15} /> Add iOS Device</button>
        </div>
      </div>

      <div style={s.statsGrid}>
        {[['Total iOS', devices.length, '#007aff'], ['Active', devices.filter(d => d.status === 'Active').length, '#10b981'], ['Non-Compliant', devices.filter(d => d.complianceStatus === 'Non-Compliant').length, '#ef4444'], ['iPhones', devices.filter(d => d.model?.toLowerCase().includes('iphone')).length, '#007aff']].map(([l, v, c]) => (
          <div key={l} style={{ ...s.statCard, borderLeftColor: c }}>
            <div style={s.statLabel}>{l}</div>
            <div style={{ ...s.statVal, color: c }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={s.filterBar}>
        <div style={s.searchWrap}>
          <Search size={15} style={s.searchIco} />
          <input type="text" placeholder="Search iOS devices..." style={s.searchIn} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <select style={s.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Active">Active</option><option value="Inactive">Inactive</option><option value="Lost">Lost</option>
        </select>
      </div>

      <div style={s.card}>
        {loading ? <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading iOS devices...</div>
          : paginated.length === 0 ? <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No iOS devices found.</div>
          : (
            <>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Device</th>
                    <th style={s.th}>Assigned To</th>
                    <th style={s.th}>iOS Version</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Compliance</th>
                    <th style={s.th}>Battery</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(dev => (
                    <tr key={dev.id}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '34px', height: '34px', borderRadius: '8px', backgroundColor: '#007aff15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Tablet size={16} color="#007aff" />
                          </div>
                          <div>
                            <div style={{ fontWeight: '500' }}>{dev.deviceName}</div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>{dev.serialNumber}</div>
                          </div>
                        </div>
                      </td>
                      <td style={s.td}><div>{dev.assignedTo || '—'}</div><div style={{ fontSize: '11px', color: '#64748b' }}>{dev.department}</div></td>
                      <td style={s.td}><span style={{ fontSize: '12px', color: '#007aff', fontWeight: '600' }}>{dev.osVersion || '—'}</span></td>
                      <td style={s.td}><span style={{ ...s.badge, backgroundColor: `${dev.getStatusColor()}15`, color: dev.getStatusColor() }}>{dev.status}</span></td>
                      <td style={s.td}><span style={{ ...s.badge, backgroundColor: `${dev.getComplianceColor()}15`, color: dev.getComplianceColor() }}>{dev.complianceStatus}</span></td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Battery size={13} color={dev.batteryLevel < 20 ? '#ef4444' : '#10b981'} />
                          <span style={{ fontWeight: '600', fontSize: '12px', color: dev.batteryLevel < 20 ? '#ef4444' : '#10b981' }}>{dev.batteryLevel}%</span>
                        </div>
                      </td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          <button style={s.actBtn} onClick={() => { setSelected(dev); setShowView(true); }}><Eye size={14} color="#64748b" /></button>
                          <button style={s.actBtn} onClick={() => openEdit(dev)}><Edit2 size={14} color="#3b82f6" /></button>
                          <button style={s.actBtn} onClick={() => openDelete(dev)}><Trash2 size={14} color="#ef4444" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={s.pager}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>{filtered.length} devices</span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button style={{ ...s.btnPrimary, ...s.btnSecond, padding: '7px 14px', fontSize: '13px' }} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</button>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>{currentPage}/{totalPages || 1}</span>
                  <button style={{ ...s.btnPrimary, ...s.btnSecond, padding: '7px 14px', fontSize: '13px' }} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
                </div>
              </div>
            </>
          )}
      </div>

      {showAdd && (<div style={s.modal} onClick={() => setShowAdd(false)}><div style={s.mContent} onClick={e => e.stopPropagation()}><div style={s.mHeader}><h2 style={s.mTitle}>Add iOS Device</h2><button style={s.actBtn} onClick={() => setShowAdd(false)}><X size={18} /></button></div><form onSubmit={handleAddSubmit}><FormBody /><div style={s.mFooter}><button type="button" style={{ ...s.btnPrimary, ...s.btnSecond }} onClick={() => setShowAdd(false)}>Cancel</button><button type="submit" style={s.btnPrimary} disabled={submitting}>{submitting ? 'Adding...' : 'Add Device'}</button></div></form></div></div>)}
      {showEdit && selected && (<div style={s.modal} onClick={() => setShowEdit(false)}><div style={s.mContent} onClick={e => e.stopPropagation()}><div style={s.mHeader}><h2 style={s.mTitle}>Edit — {selected.deviceName}</h2><button style={s.actBtn} onClick={() => setShowEdit(false)}><X size={18} /></button></div><form onSubmit={handleEditSubmit}><FormBody /><div style={s.mFooter}><button type="button" style={{ ...s.btnPrimary, ...s.btnSecond }} onClick={() => setShowEdit(false)}>Cancel</button><button type="submit" style={s.btnPrimary} disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button></div></form></div></div>)}
      {showView && selected && (<div style={s.modal} onClick={() => setShowView(false)}><div style={s.mContent} onClick={e => e.stopPropagation()}><div style={s.mHeader}><h2 style={s.mTitle}>{selected.deviceName}</h2><button style={s.actBtn} onClick={() => setShowView(false)}><X size={18} /></button></div><div style={s.mBody}><div style={s.grid2}>{[['Model', selected.model], ['Serial', selected.serialNumber], ['iOS', selected.osVersion || '—'], ['IMEI', selected.imei || '—'], ['Assigned To', selected.assignedTo || '—'], ['Battery', `${selected.batteryLevel}%`]].map(([l, v]) => (<div key={l}><div style={s.detLabel}>{l}</div><div style={s.detVal}>{v}</div></div>))}</div></div><div style={s.mFooter}><button style={{ ...s.btnPrimary, ...s.btnSecond }} onClick={() => { setShowView(false); openEdit(selected); }}>Edit</button><button style={s.btnPrimary} onClick={() => setShowView(false)}>Close</button></div></div></div>)}
      {showDelete && selected && (<div style={s.modal} onClick={() => setShowDelete(false)}><div style={{ ...s.mContent, maxWidth: '380px' }} onClick={e => e.stopPropagation()}><div style={s.mHeader}><h2 style={s.mTitle}>Remove Device</h2><button style={s.actBtn} onClick={() => setShowDelete(false)}><X size={18} /></button></div><div style={s.mBody}><div style={{ textAlign: 'center' }}><div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Trash2 size={24} color="#ef4444" /></div><p style={{ color: '#1e293b', fontWeight: '500' }}>Remove "{selected.deviceName}"?</p></div></div><div style={s.mFooter}><button style={{ ...s.btnPrimary, ...s.btnSecond }} onClick={() => setShowDelete(false)}>Cancel</button><button style={{ ...s.btnPrimary, ...s.btnDanger }} onClick={handleDelete}>Remove</button></div></div></div>)}
    </div>
  );
};

export default IOSDevicesPage;

